import { useEffect, useMemo, useRef, useState } from 'react';
import { BaseURL } from '@/const/serviceEnv';
import { Listener, MessageEnd, ssePost } from '@/tools/sse';

interface LinsterEvents {
  chatList: (list: Message[]) => void;
  loadingChange: (loading: boolean) => void;
}
export interface Message {
  messageId: string;
  conversationId: string;
  question: string;
  answer: string;
  loading: boolean;
  error: string;
  suggests: Array<{ title: string; ref_id: string; ref_type: string }>;
  abort?: () => void;
  regenerate?: () => void;
  readNum?: number;
}

enum FlagStatus {
  PENDING = 0,
  PROCESSING = 1,
  FINISHED = 2,
}

class Waiter {
  private promise: Promise<void> | undefined;

  constructor() {
    this.receive();
  }

  /** 接收任务 */
  receive() {
    if (this.promise) {
      return;
    }

    this.promise = new Promise<void>((resolve, reject) => {
      this.complete = () => {
        resolve();
      };
      this.reject = () => {
        reject(new Error('Waiter refused.'));
      };
    });
  }

  /** 完成任务 */
  complete() {}

  /** 回绝任务 */
  reject() {}

  /** 等待任务结束 */
  get ready() {
    return Promise.resolve(this.promise) as Promise<void>;
  }
}

class SeePost {
  private readonly listener = new Listener<Readonly<LinsterEvents>>();

  private chatList: Message[] = [];

  get on() {
    return this.listener.on.bind(this.listener);
  }

  get off() {
    return this.listener.off.bind(this.listener);
  }

  constructor(private url: string, private needSuggest: boolean) {}

  send = (data: Record<string, any>) => {
    const updateChatList = () => {
      this.listener.fire('chatList', [...this.chatList]);
    };

    const responseData: Message = {
      messageId: '',
      conversationId: '',
      question: data.inputMessage,
      answer: '',
      loading: true,
      error: '',
      suggests: [],
    };

    let msgFlag = FlagStatus.PENDING;
    let typeFlag = FlagStatus.PENDING;

    const typingEnd = new Waiter();

    let answer = '';
    let answerTypeIndex = 0;

    const typeAnswerEffect = (speed = 20) => {
      if (typeFlag === FlagStatus.PROCESSING) return;
      const type = () => {
        if (typeFlag === FlagStatus.FINISHED) return;

        if (answerTypeIndex < answer.length) {
          typeFlag = FlagStatus.PROCESSING;
          responseData.answer += answer[answerTypeIndex++];
          updateChatList();
          setTimeout(type, speed);
        } else if (msgFlag === FlagStatus.FINISHED && answerTypeIndex === answer.length) {
          typeFlag = FlagStatus.FINISHED;

          typingEnd.complete();
        } else {
          typeFlag = FlagStatus.PENDING;
        }
      };
      type();
    };

    const loadSuggests = (source: any[]) => {
      typingEnd.ready.then(() => {
        responseData.suggests = source;
        responseData.loading = false;
        updateChatList();
      });
    };

    const request = (data: Record<string, any>) =>
      ssePost(this.url, data, {
        onNodeFinished(node) {
          if (node.data?.node_type === 'knowledge-retrieval') {
            responseData.readNum = node.data.outputs.result?.length;
            updateChatList();
          }
        },
        onData(message, _, moreInfo) {
          responseData.conversationId = moreInfo.conversationId || '';
          responseData.messageId = moreInfo.messageId;

          answer += message;
          typeAnswerEffect();
        },
        onCompleted(hasError, msg) {
          responseData.error = hasError ? `${msg || ''}` : ``;

          msgFlag = FlagStatus.FINISHED;
        },
        async onMessageEnd(messageEnd: MessageEnd) {
          msgFlag = FlagStatus.FINISHED;
          loadSuggests(messageEnd.metadata.retriever_resources || []);
        },
      });

    let task = request(data);

    const abort = () => {
      responseData.loading = false;
      msgFlag = FlagStatus.FINISHED;
      typeFlag = FlagStatus.FINISHED;
      typingEnd.complete();
      task.abort();
      updateChatList();
    };

    const regenerate = () => {
      responseData.loading = true;
      responseData.answer = '';
      responseData.suggests = [];

      msgFlag = FlagStatus.PENDING;
      typeFlag = FlagStatus.PENDING;

      typingEnd.receive();

      answer = '';
      answerTypeIndex = 0;

      task = request({ ...data, parentMessageId: responseData.messageId });

      updateChatList();
    };

    responseData.abort = abort;
    responseData.regenerate = regenerate;
    this.chatList.push(responseData);
    updateChatList();
  };
}

export function useSeePost(options?: { chatListDomRef?: React.RefObject<HTMLDivElement> }) {
  const chatListDomRef = useRef<HTMLDivElement>(null); // 对话容器
  const [chatList, setChatList] = useState<Message[]>([]);

  const ssePostRef = useRef<SeePost>();

  const bottomFlagRef = useRef(true);

  const isChatting = useMemo(() => {
    return chatList.length ? chatList[chatList.length - 1].loading : false;
  }, [chatList]);

  const send = (data: Record<string, any>) => {
    if (!ssePostRef.current) {
      ssePostRef.current = new SeePost(`${BaseURL}/program/fct/assistant/chat-messages`, true);
      ssePostRef.current.on('chatList', setChatList);
    }

    ssePostRef.current.send(data);
  };

  const abort = () => {
    chatList.find((item) => item.loading)?.abort?.();
  };

  const onScrollRef = useRef(() => {
    const el = options?.chatListDomRef?.current || chatListDomRef.current;
    if (el) {
      const maxScrollTop = el.scrollHeight - el.clientHeight;
      if (el.scrollTop === maxScrollTop) {
        bottomFlagRef.current = true;
      } else {
        bottomFlagRef.current = false;
      }
    }
  });
  const toBottomRef = useRef(() => {
    const el = options?.chatListDomRef?.current || chatListDomRef.current;
    if (el && bottomFlagRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  });

  useEffect(() => {
    const el = options?.chatListDomRef?.current || chatListDomRef.current;

    if (el) {
      el.addEventListener('scroll', onScrollRef.current);
      return () => {
        el.removeEventListener('scroll', onScrollRef.current);
      };
    }
  }, []);

  useEffect(() => {
    toBottomRef.current();
  }, [chatList]);

  return { chatListDomRef, chatList, isChatting, send, abort };
}
